import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShieldCheck, Zap, Eye,
  ExternalLink, Gift, Sparkles, Settings, LogOut, Users, BarChart3, 
  Save, RefreshCw, Trash2, Mail, KeyRound, Send, Instagram, Twitter,
  Youtube, MessageCircle, ChevronLeft, ChevronRight, Coins, Bot, X,
  Clock, Plus, Play, Pause, Calendar, Globe, ToggleLeft, ToggleRight
} from "lucide-react";
import TelegramMiniApp from "./components/TelegramMiniApp";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Switch } from "./components/ui/switch";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Social media icons
const SocialIcons = {
  telegram: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  ),
  discord: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
    </svg>
  ),
  tiktok: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  )
};

// Disable copy
const disableCopy = (e) => {
  e.preventDefault();
  return false;
};

// Animated Logo Component
const AnimatedLogo = ({ settings }) => (
  <motion.div 
    className="flex items-center gap-4"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  >
    <motion.div 
      className="relative"
      animate={{ 
        rotateY: [0, 360],
        scale: [1, 1.1, 1]
      }}
      transition={{ 
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      <div className="w-16 h-16 bg-gradient-to-br from-[#CCFF00] via-[#00FFFF] to-[#FF0099] rounded-xl flex items-center justify-center shadow-2xl shadow-[#CCFF00]/30">
        <Gift className="w-8 h-8 text-black" />
      </div>
      <motion.div 
        className="absolute -top-2 -right-2 w-6 h-6 bg-[#FF0099] rounded-full flex items-center justify-center"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
      >
        <Sparkles className="w-3 h-3 text-white" />
      </motion.div>
    </motion.div>
    <div className="flex flex-col">
      <motion.span 
        className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight"
        animate={{ 
          textShadow: [
            "0 0 10px #CCFF00",
            "0 0 20px #00FFFF",
            "0 0 10px #FF0099",
            "0 0 10px #CCFF00"
          ]
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        PROMO CODE
      </motion.span>
      <span className="text-xs font-mono text-[#00FFFF] uppercase tracking-[0.3em]">
        Service
      </span>
    </div>
  </motion.div>
);

// Slider Component
const Slider = ({ messages }) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (messages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % messages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [messages.length]);

  if (!messages || messages.length === 0) return null;

  return (
    <div className="w-full max-w-lg mx-auto mb-8">
      <div className="relative overflow-hidden h-16 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="absolute text-center px-4"
          >
            <p className="text-[#A1A1AA] text-sm sm:text-base">
              {messages[current]}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
      {messages.length > 1 && (
        <div className="flex justify-center gap-2 mt-2">
          {messages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === current ? "bg-[#CCFF00] w-6" : "bg-[#2A2A2A]"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Social Media Buttons
const SocialMediaButtons = ({ socialMedia }) => {
  if (!socialMedia) return null;

  const platforms = [
    { key: "telegram", icon: SocialIcons.telegram, label: "Telegram" },
    { key: "instagram", icon: Instagram, label: "Instagram" },
    { key: "twitter", icon: Twitter, label: "Twitter/X" },
    { key: "youtube", icon: Youtube, label: "YouTube" },
    { key: "tiktok", icon: SocialIcons.tiktok, label: "TikTok" },
    { key: "discord", icon: SocialIcons.discord, label: "Discord" }
  ];

  const enabledPlatforms = platforms.filter(p => socialMedia[p.key]?.enabled && socialMedia[p.key]?.url);

  if (enabledPlatforms.length === 0) return null;

  // Find primary (telegram)
  const primary = enabledPlatforms.find(p => socialMedia[p.key]?.is_primary);
  const others = enabledPlatforms.filter(p => !socialMedia[p.key]?.is_primary);

  return (
    <div className="flex flex-col items-center gap-4 mt-8">
      {primary && (
        <motion.a
          href={socialMedia[primary.key].url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-6 py-3 bg-[#0088cc]/20 border border-[#0088cc]/50 text-[#0088cc] hover:bg-[#0088cc]/30 transition-all rounded-lg"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="primary-social-btn"
        >
          <primary.icon className="w-6 h-6" />
          <span className="font-display uppercase tracking-wider">{primary.label}</span>
          <span className="text-xs bg-[#FF0099] text-white px-2 py-0.5 rounded">EN POPÜLER</span>
        </motion.a>
      )}
      
      <div className="flex flex-wrap justify-center gap-3">
        {others.map((platform) => (
          <motion.a
            key={platform.key}
            href={socialMedia[platform.key].url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 bg-[#2A2A2A] border border-[#3A3A3A] text-white hover:border-[#CCFF00] hover:text-[#CCFF00] transition-all rounded-lg"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            data-testid={`social-btn-${platform.key}`}
          >
            <platform.icon className="w-5 h-5" />
          </motion.a>
        ))}
      </div>
    </div>
  );
};

// AI Chatbot Component
const Chatbot = ({ enabled, onClose }) => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Merhaba! Size nasıl yardımcı olabilirim? Promosyon kodları, jeton sistemi veya platform hakkında sorularınızı yanıtlayabilirim." }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });
      
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (e) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Üzgünüm, şu anda yanıt veremiyorum. Lütfen daha sonra tekrar deneyin." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!enabled) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 100, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 100, scale: 0.8 }}
      className="fixed bottom-4 right-4 w-80 sm:w-96 h-[500px] bg-[#121212] border border-[#CCFF00]/30 rounded-lg shadow-2xl shadow-[#CCFF00]/10 flex flex-col z-50"
      data-testid="chatbot-window"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-[#CCFF00]" />
          <span className="font-display text-white">Canlı Destek</span>
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        </div>
        <button onClick={onClose} className="text-[#A1A1AA] hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-lg text-sm ${
                msg.role === "user"
                  ? "bg-[#CCFF00] text-black"
                  : "bg-[#2A2A2A] text-white"
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-[#2A2A2A] p-3 rounded-lg">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-[#CCFF00] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-[#CCFF00] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-[#CCFF00] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-[#2A2A2A]">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Mesajınızı yazın..."
            className="flex-1 bg-[#2A2A2A] border-[#3A3A3A] text-white"
            data-testid="chatbot-input"
          />
          <Button
            onClick={sendMessage}
            disabled={isLoading || !input.trim()}
            className="btn-glow"
            data-testid="chatbot-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Chatbot Toggle Button
const ChatbotToggle = ({ onClick, enabled }) => {
  if (!enabled) return null;

  return (
    <motion.button
      onClick={onClick}
      className="fixed bottom-20 right-4 w-14 h-14 bg-gradient-to-br from-[#CCFF00] to-[#00FFFF] rounded-full flex items-center justify-center shadow-lg shadow-[#CCFF00]/30 z-40"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      data-testid="chatbot-toggle"
    >
      <Bot className="w-6 h-6 text-black" />
    </motion.button>
  );
};

// Token Display Component - removed (fingerprint system deprecated)

// Recommendation Carousel - 5 saniyede bir firma degisir
const RecommendationCarousel = ({ recommendations }) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (recommendations.length <= 1) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % recommendations.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [recommendations.length]);

  if (!recommendations || recommendations.length === 0) {
    return (
      <div className="mb-6" data-testid="recommendations-section">
        <p className="text-xs font-mono text-[#FF0099] uppercase tracking-wider text-center mb-4">
          Gunun Onerilen Firmalari!
        </p>
        <div className="text-center py-6" data-testid="no-recommendations">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-[#A1A1AA]/30" />
          <p className="text-[#A1A1AA] text-sm">Yeni oneriler yakinda!</p>
          <p className="text-[#A1A1AA]/50 text-xs mt-1">Takipte kalin!</p>
        </div>
      </div>
    );
  }

  const rec = recommendations[current];

  return (
    <div className="mb-6" data-testid="recommendations-section">
      <p className="text-xs font-mono text-[#FF0099] uppercase tracking-wider text-center mb-4">
        Gunun Onerilen Firmalari!
      </p>
      <div className="relative overflow-hidden min-h-[100px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={rec.id}
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -60 }}
            transition={{ duration: 0.4 }}
            className="p-4 bg-[#0a0a0a] border border-[#FF0099]/30 rounded-lg"
            data-testid={`recommendation-card-${rec.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-display text-base font-bold truncate">{rec.name}</h3>
                {rec.description && (
                  <p className="text-[#A1A1AA] text-sm mt-1">{rec.description}</p>
                )}
                {rec.code && (
                  <p className="text-[#CCFF00] text-xs font-mono mt-2">Kod: {rec.code}</p>
                )}
              </div>
              {rec.url ? (
                <motion.a
                  href={rec.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-4 flex items-center gap-2 px-5 py-3 bg-[#FF0099] hover:bg-[#FF0099]/80 text-white text-sm font-display uppercase tracking-wider rounded-lg transition-all whitespace-nowrap"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`recommendation-btn-${rec.id}`}
                >
                  <ExternalLink className="w-4 h-4" />
                  Git
                </motion.a>
              ) : (
                <motion.a
                  href="https://t.me/PromocodeAI_bot/PROM"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-4 flex items-center gap-2 px-5 py-3 bg-[#0088cc] hover:bg-[#0099dd] text-white text-sm font-display uppercase tracking-wider rounded-lg transition-all whitespace-nowrap"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`recommendation-tg-btn-${rec.id}`}
                >
                  <SocialIcons.telegram className="w-4 h-4" />
                  Kod Al
                </motion.a>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      {/* Dots indicator */}
      {recommendations.length > 1 && (
        <div className="flex justify-center gap-2 mt-3">
          {recommendations.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === current ? "bg-[#FF0099] w-6" : "bg-[#2A2A2A]"
              }`}
              data-testid={`recommendation-dot-${idx}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Home Page Component
const Home = () => {
  const [settings, setSettings] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [showChatbot, setShowChatbot] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, recsRes] = await Promise.all([
          axios.get(`${API}/settings/public`),
          axios.get(`${API}/recommendations`)
        ]);
        setSettings(settingsRes.data);
        setRecommendations(recsRes.data.recommendations || []);
      } catch (e) {
        console.error("Fetch error:", e);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="cyber-bg grid-pattern min-h-screen"
      onContextMenu={disableCopy}
      onCopy={disableCopy}
      onCut={disableCopy}
    >
      <div className="noise-overlay" />

      <div className="hero-container">
        {/* Status Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-[#CCFF00]/20 px-4 py-2"
        >
          <div className="max-w-4xl mx-auto flex items-center justify-between text-xs font-mono">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-[#CCFF00] to-[#00FFFF] rounded flex items-center justify-center">
                <Gift className="w-3 h-3 text-black" />
              </div>
              <span className="text-white font-display text-sm tracking-tight hidden sm:inline">
                {settings?.company_short_name || 'PCS'}
              </span>
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-[#A1A1AA] uppercase tracking-wider">Aktif</span>
            </div>
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <Coins className="w-3 h-3" />
              <span>Jeton Sistemi</span>
            </div>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center font-display text-lg sm:text-xl text-[#CCFF00] uppercase tracking-wider mb-4 mt-16"
          data-testid="home-title"
        >
          {settings?.title || "PROMO CODE - Ucretsiz ve Kazandiran Bonuslar!"}
        </motion.h1>

        {/* Slider */}
        <Slider messages={settings?.slider_messages || []} />

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="promo-card p-6 sm:p-8 relative"
          data-testid="promo-card"
        >
          <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#CCFF00]" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#CCFF00]" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#CCFF00]" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#CCFF00]" />

          <div className="text-center mb-6">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-center mb-6"
            >
              <AnimatedLogo settings={settings} />
            </motion.div>

            <p className="text-[#A1A1AA] text-sm">
              {settings?.tagline || "Ozel indirim kodlariniz icin tek adres"}
            </p>
          </div>

          {/* Jeton Section */}
          <div className="mb-6 p-4 bg-gradient-to-r from-[#CCFF00]/10 to-[#00FFFF]/10 border border-[#CCFF00]/30 rounded-lg" data-testid="jeton-section">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#CCFF00]/20 rounded-lg flex items-center justify-center">
                <Coins className="w-5 h-5 text-[#CCFF00]" />
              </div>
              <div>
                <h3 className="text-white font-display text-sm uppercase">Gunluk Jeton Kazan</h3>
                <p className="text-[#A1A1AA] text-xs">Her gun {settings?.daily_token_reward || 5} JETON hediye!</p>
              </div>
            </div>
            <motion.a
              href="https://t.me/PromocodeAI_bot/PROM"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-[#CCFF00] hover:bg-[#b8e600] text-black font-display uppercase tracking-wider rounded-lg transition-all text-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="jeton-cta-btn"
            >
              <SocialIcons.telegram className="w-5 h-5" />
              Telegram ile Jeton Al
            </motion.a>
            <p className="text-center text-xs text-[#A1A1AA]/60 mt-2">
              Telegram hesabiniz ile dogrulanarak jeton alabilirsiniz
            </p>
          </div>

          {/* Gunun Onerilen Firmalari - Carousel */}
          <RecommendationCarousel recommendations={recommendations} />

          {/* Telegram CTA */}
          <div className="text-center pt-4 border-t border-[#2A2A2A]">
            <motion.a
              href="https://t.me/PromocodeAI_bot/PROM"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#0088cc] hover:bg-[#0099dd] text-white font-display uppercase tracking-wider rounded-lg transition-all shadow-lg shadow-[#0088cc]/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="telegram-miniapp-btn"
            >
              <SocialIcons.telegram className="w-6 h-6" />
              Telegram Mini App
            </motion.a>
            <p className="mt-3 text-xs text-[#A1A1AA]/60">
              Kodlar ve Jetonlar icin Telegram uzerinden giris yapin
            </p>
          </div>
        </motion.div>

        {/* Social Media Buttons */}
        <SocialMediaButtons socialMedia={settings?.social_media} />

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-8 text-center space-y-4"
        >
          <div className="flex justify-center gap-4 text-xs text-[#A1A1AA]">
            <a href={`mailto:${settings?.contact_emails?.marketing}`} className="hover:text-[#CCFF00]">
              <Mail className="w-4 h-4 inline mr-1" />
              Marketing
            </a>
            <a href={`mailto:${settings?.contact_emails?.developer}`} className="hover:text-[#00FFFF]">
              <Mail className="w-4 h-4 inline mr-1" />
              Developer API
            </a>
          </div>
          <p className="text-xs font-mono text-[#A1A1AA]/50 uppercase tracking-wider">
            Telegram ile Korunuyor
          </p>
        </motion.div>
      </div>

      {/* Chatbot */}
      <AnimatePresence>
        {showChatbot ? (
          <Chatbot enabled={settings?.chatbot_enabled} onClose={() => setShowChatbot(false)} />
        ) : (
          <ChatbotToggle onClick={() => setShowChatbot(true)} enabled={settings?.chatbot_enabled} />
        )}
      </AnimatePresence>

      <Toaster position="top-center" richColors />
    </div>
  );
};

// Admin Login Component
const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem("admin_token");
      if (token) {
        try {
          await axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } });
          navigate("/admindev/dashboard");
        } catch { localStorage.removeItem("admin_token"); }
      }
      setIsChecking(false);
    };
    checkToken();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedPassword = password.trim();
      const res = await axios.post(`${API}/admin/login`, { email: trimmedEmail, password: trimmedPassword });
      if (res.data.success) {
        localStorage.setItem("admin_token", res.data.token);
        toast.success("Giriş başarılı!");
        navigate("/admindev/dashboard");
      } else {
        toast.error(res.data.message || "Geçersiz email veya şifre");
      }
    } catch (err) {
      toast.error("Giriş başarısız. Lütfen tekrar deneyin.");
    }
    finally { setIsLoading(false); }
  };

  if (isChecking) {
    return <div className="cyber-bg min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#CCFF00]/30 border-t-[#CCFF00] rounded-full animate-spin" />
    </div>;
  }

  return (
    <div className="cyber-bg grid-pattern min-h-screen flex items-center justify-center p-4">
      <div className="noise-overlay" />
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="promo-card p-8 w-full max-w-md relative z-10">
        <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#CCFF00]" />
        <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#CCFF00]" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#CCFF00]" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#CCFF00]" />

        <div className="text-center mb-8">
          <Settings className="w-16 h-16 mx-auto mb-4 text-[#CCFF00]" />
          <h1 className="font-display text-2xl font-bold text-white uppercase">Admin Panel</h1>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <Label className="text-[#A1A1AA]">Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="bg-[#121212] border-[#2A2A2A] text-white" required />
          </div>
          <div>
            <Label className="text-[#A1A1AA]">Şifre</Label>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" className="bg-[#121212] border-[#2A2A2A] text-white" required />
          </div>
          <Button type="submit" disabled={isLoading} className="w-full btn-glow py-6 h-auto">
            {isLoading ? "Giriş Yapılıyor..." : "Giriş Yap"}
          </Button>
        </form>
      </motion.div>
      <Toaster position="top-center" richColors />
    </div>
  );
};

// Admin Dashboard Component - Simplified for space, will be in next file
const AdminDashboard = () => {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [campaigns, setCampaigns] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [botStats, setBotStats] = useState(null);
  const [newCampaign, setNewCampaign] = useState({ name: "", code: "", description: "", redirect_url: "", active: true });
  const [newRec, setNewRec] = useState({ name: "", code: "", description: "", url: "", active: true });
  const [newPost, setNewPost] = useState({ message: "", hour: 12, minute: 0, enabled: true, days: ["mon","tue","wed","thu","fri","sat","sun"] });
  const navigate = useNavigate();
  const token = localStorage.getItem("admin_token");

  const axiosAuth = axios.create({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    if (!token) { navigate("/admindev"); return; }
    const fetch = async () => {
      try {
        await axiosAuth.get(`${API}/admin/verify`);
        const [s, st, camp, recs, posts, bStats] = await Promise.all([
          axiosAuth.get(`${API}/admin/settings`),
          axiosAuth.get(`${API}/admin/stats`),
          axiosAuth.get(`${API}/admin/campaigns`).catch(() => ({ data: { campaigns: [] } })),
          axiosAuth.get(`${API}/admin/recommendations`).catch(() => ({ data: { recommendations: [] } })),
          axiosAuth.get(`${API}/admin/scheduled-posts`).catch(() => ({ data: { posts: [] } })),
          axiosAuth.get(`${API}/admin/telegram/stats`).catch(() => ({ data: null }))
        ]);
        setSettings(s.data);
        setStats(st.data);
        setCampaigns(camp.data.campaigns || []);
        setRecommendations(recs.data.recommendations || []);
        setScheduledPosts(posts.data.posts || []);
        setBotStats(bStats.data);
      } catch { localStorage.removeItem("admin_token"); navigate("/admindev"); }
      finally { setIsLoading(false); }
    };
    fetch();
  }, [token, navigate]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await axiosAuth.put(`${API}/admin/settings`, settings);
      setSettings(res.data);
      toast.success("Ayarlar kaydedildi!");
    } catch { toast.error("Kaydetme başarısız!"); }
    finally { setIsSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm("Tüm cihaz kayıtlarını silmek istediğinizden emin misiniz?")) return;
    try {
      const res = await axiosAuth.delete(`${API}/admin/reset-devices`);
      toast.success(`${res.data.deleted} kayıt silindi!`);
      const st = await axiosAuth.get(`${API}/admin/stats`);
      setStats(st.data);
    } catch { toast.error("Silme başarısız!"); }
  };

  const testTelegram = async () => {
    try {
      const res = await axiosAuth.post(`${API}/admin/telegram/test`, { message: "Test mesajı - Promo Code Service" });
      if (res.data.success) toast.success("Telegram mesajı gönderildi!");
      else toast.error(res.data.message);
    } catch { toast.error("Telegram testi başarısız!"); }
  };

  // Campaign CRUD
  const addCampaign = async () => {
    if (!newCampaign.name || !newCampaign.code) { toast.error("Ad ve kod zorunlu!"); return; }
    try {
      const res = await axiosAuth.post(`${API}/admin/campaigns`, newCampaign);
      setCampaigns([res.data.campaign, ...campaigns]);
      setNewCampaign({ name: "", code: "", description: "", redirect_url: "", active: true });
      toast.success("Kampanya eklendi!");
    } catch { toast.error("Ekleme başarısız!"); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Kampanyayı silmek istediğinizden emin misiniz?")) return;
    try {
      await axiosAuth.delete(`${API}/admin/campaigns/${id}`);
      setCampaigns(campaigns.filter(c => c.id !== id));
      toast.success("Kampanya silindi!");
    } catch { toast.error("Silme başarısız!"); }
  };

  const toggleCampaign = async (id, active) => {
    try {
      await axiosAuth.put(`${API}/admin/campaigns/${id}`, { active });
      setCampaigns(campaigns.map(c => c.id === id ? { ...c, active } : c));
    } catch { toast.error("Güncelleme başarısız!"); }
  };

  // Recommendation CRUD
  const addRecommendation = async () => {
    if (!newRec.name || !newRec.code) { toast.error("Ad ve kod zorunlu!"); return; }
    try {
      const res = await axiosAuth.post(`${API}/admin/recommendations`, newRec);
      setRecommendations([...recommendations, res.data.recommendation]);
      setNewRec({ name: "", code: "", description: "", url: "", active: true });
      toast.success("Öneri eklendi!");
    } catch { toast.error("Ekleme başarısız!"); }
  };

  const deleteRecommendation = async (id) => {
    if (!window.confirm("Öneriyi silmek istediğinizden emin misiniz?")) return;
    try {
      await axiosAuth.delete(`${API}/admin/recommendations/${id}`);
      setRecommendations(recommendations.filter(r => r.id !== id));
      toast.success("Öneri silindi!");
    } catch { toast.error("Silme başarısız!"); }
  };

  const toggleRecommendation = async (id, active) => {
    try {
      await axiosAuth.put(`${API}/admin/recommendations/${id}`, { active });
      setRecommendations(recommendations.map(r => r.id === id ? { ...r, active } : r));
    } catch { toast.error("Güncelleme başarısız!"); }
  };

  // Scheduled Posts CRUD
  const addScheduledPost = async () => {
    if (!newPost.message) { toast.error("Mesaj zorunlu!"); return; }
    try {
      const res = await axiosAuth.post(`${API}/admin/scheduled-posts`, newPost);
      setScheduledPosts([...scheduledPosts, res.data.post]);
      setNewPost({ message: "", hour: 12, minute: 0, enabled: true, days: ["mon","tue","wed","thu","fri","sat","sun"] });
      toast.success("Zamanlanmış gönderi eklendi!");
    } catch { toast.error("Ekleme başarısız!"); }
  };

  const deleteScheduledPost = async (id) => {
    if (!window.confirm("Zamanlanmış gönderiyi silmek istediğinizden emin misiniz?")) return;
    try {
      await axiosAuth.delete(`${API}/admin/scheduled-posts/${id}`);
      setScheduledPosts(scheduledPosts.filter(p => p.id !== id));
      toast.success("Gönderi silindi!");
    } catch { toast.error("Silme başarısız!"); }
  };

  const toggleScheduledPost = async (id, enabled) => {
    try {
      await axiosAuth.put(`${API}/admin/scheduled-posts/${id}`, { enabled });
      setScheduledPosts(scheduledPosts.map(p => p.id === id ? { ...p, enabled } : p));
    } catch { toast.error("Güncelleme başarısız!"); }
  };

  if (isLoading) {
    return <div className="cyber-bg min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#CCFF00]/30 border-t-[#CCFF00] rounded-full animate-spin" />
    </div>;
  }

  const tabs = [
    { id: "general", label: "Genel", icon: Settings },
    { id: "campaigns", label: "Kampanyalar", icon: Gift },
    { id: "recommendations", label: "Öneriler", icon: Sparkles },
    { id: "social", label: "Sosyal Medya", icon: Globe },
    { id: "telegram", label: "Telegram", icon: Send },
    { id: "scheduled", label: "Zamanlanmış", icon: Clock },
    { id: "chatbot", label: "AI Chatbot", icon: Bot },
    { id: "stats", label: "İstatistikler", icon: BarChart3 }
  ];

  return (
    <div className="cyber-bg grid-pattern min-h-screen">
      <div className="noise-overlay" />
      
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-[#CCFF00]/20 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-[#CCFF00]" />
            <span className="font-display text-lg font-bold text-white">Admin Panel</span>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleSave} disabled={isSaving} className="btn-glow" size="sm">
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? "..." : "Kaydet"}
            </Button>
            <Button onClick={() => { localStorage.removeItem("admin_token"); navigate("/admindev"); }} variant="ghost" size="sm" className="text-[#A1A1AA]">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="pt-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-2 mb-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-sm uppercase tracking-wider transition-all ${
                  activeTab === tab.id ? "bg-[#CCFF00] text-black" : "bg-[#2A2A2A] text-[#A1A1AA] hover:text-white"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="relative z-10 space-y-6 pb-8">
            {activeTab === "general" && settings && (
              <div className="promo-card p-6 space-y-6">
                <h2 className="font-display text-lg text-white uppercase flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#CCFF00]" /> Genel Ayarlar
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#A1A1AA]">Promosyon Kodu</Label>
                    <Input value={settings.promo_code || ""} onChange={(e) => setSettings({...settings, promo_code: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white font-mono" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Yönlendirme URL</Label>
                    <Input value={settings.redirect_url || ""} onChange={(e) => setSettings({...settings, redirect_url: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Sayfa Başlığı</Label>
                    <Input value={settings.title || ""} onChange={(e) => setSettings({...settings, title: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Slogan</Label>
                    <Input value={settings.tagline || ""} onChange={(e) => setSettings({...settings, tagline: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Geri Sayım (saniye)</Label>
                    <Input type="number" value={settings.countdown_seconds || 60} onChange={(e) => setSettings({...settings, countdown_seconds: parseInt(e.target.value)})} className="bg-[#121212] border-[#2A2A2A] text-white" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Günlük Jeton</Label>
                    <Input type="number" value={settings.daily_token_reward || 5} onChange={(e) => setSettings({...settings, daily_token_reward: parseInt(e.target.value)})} className="bg-[#121212] border-[#2A2A2A] text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Slider Mesajları (her satır bir mesaj)</Label>
                  <Textarea 
                    value={(settings.slider_messages || []).join("\n")} 
                    onChange={(e) => setSettings({...settings, slider_messages: e.target.value.split("\n").filter(s => s.trim())})}
                    className="bg-[#121212] border-[#2A2A2A] text-white h-32"
                  />
                </div>
              </div>
            )}

            {activeTab === "campaigns" && (
              <div className="space-y-6">
                {/* Yeni Kampanya Ekle */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <Plus className="w-5 h-5 text-[#CCFF00]" /> Yeni Kampanya Ekle
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Kampanya Adı</Label>
                      <Input value={newCampaign.name} onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white" placeholder="Yılbaşı Kampanyası" />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Promosyon Kodu</Label>
                      <Input value={newCampaign.code} onChange={(e) => setNewCampaign({...newCampaign, code: e.target.value.toUpperCase()})} className="bg-[#121212] border-[#2A2A2A] text-white font-mono" placeholder="YILBASI2026" />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Yönlendirme URL</Label>
                      <Input value={newCampaign.redirect_url} onChange={(e) => setNewCampaign({...newCampaign, redirect_url: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white" placeholder="https://example.com" />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Açıklama</Label>
                      <Input value={newCampaign.description} onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white" placeholder="Kampanya açıklaması" />
                    </div>
                  </div>
                  <Button onClick={addCampaign} className="mt-4 btn-glow">
                    <Plus className="w-4 h-4 mr-2" /> Kampanya Ekle
                  </Button>
                </div>

                {/* Kampanya Listesi */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <Gift className="w-5 h-5 text-[#FF0099]" /> Kampanyalar ({campaigns.length})
                  </h2>
                  <div className="space-y-3">
                    {campaigns.map(camp => (
                      <div key={camp.id} className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                        <div className="flex items-center gap-4">
                          <Switch checked={camp.active} onCheckedChange={(v) => toggleCampaign(camp.id, v)} />
                          <div>
                            <p className="text-white font-display">{camp.name}</p>
                            <p className="text-xs text-[#A1A1AA]">Kod: <span className="text-[#CCFF00] font-mono">{camp.code}</span> | Görüntüleme: {camp.view_count || 0}</p>
                          </div>
                        </div>
                        <Button onClick={() => deleteCampaign(camp.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {campaigns.length === 0 && <p className="text-center text-[#A1A1AA] py-8">Henüz kampanya yok</p>}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "recommendations" && (
              <div className="space-y-6">
                {/* Yeni Öneri Ekle */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <Plus className="w-5 h-5 text-[#CCFF00]" /> Yeni Öneri Ekle
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Öneri Adı</Label>
                      <Input value={newRec.name} onChange={(e) => setNewRec({...newRec, name: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white" placeholder="🎰 Casino Bonus" />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Promosyon Kodu</Label>
                      <Input value={newRec.code} onChange={(e) => setNewRec({...newRec, code: e.target.value.toUpperCase()})} className="bg-[#121212] border-[#2A2A2A] text-white font-mono" placeholder="CASINO50" />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Açıklama</Label>
                      <Input value={newRec.description} onChange={(e) => setNewRec({...newRec, description: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white" placeholder="%50 Hoşgeldin Bonusu" />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Yönlendirme URL</Label>
                      <Input value={newRec.url} onChange={(e) => setNewRec({...newRec, url: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white" placeholder="https://firma.com" />
                    </div>
                  </div>
                  <Button onClick={addRecommendation} className="mt-4 btn-glow">
                    <Plus className="w-4 h-4 mr-2" /> Öneri Ekle
                  </Button>
                </div>

                {/* Öneri Listesi */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-[#00FFFF]" /> Öneriler ({recommendations.length})
                  </h2>
                  <div className="space-y-3">
                    {recommendations.map(rec => (
                      <div key={rec.id} className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                        <div className="flex items-center gap-4">
                          <Switch checked={rec.active} onCheckedChange={(v) => toggleRecommendation(rec.id, v)} />
                          <div>
                            <p className="text-white font-display">{rec.name}</p>
                            <p className="text-xs text-[#A1A1AA]">Kod: <span className="text-[#CCFF00] font-mono">{rec.code}</span> | {rec.description}</p>
                            {rec.url && <p className="text-xs text-[#00FFFF] truncate max-w-xs">{rec.url}</p>}
                          </div>
                        </div>
                        <Button onClick={() => deleteRecommendation(rec.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {recommendations.length === 0 && <p className="text-center text-[#A1A1AA] py-8">Henüz öneri yok</p>}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "social" && settings && (
              <div className="promo-card p-6 space-y-6">
                <h2 className="font-display text-lg text-white uppercase flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#00FFFF]" /> Sosyal Medya
                </h2>
                {["telegram", "instagram", "twitter", "youtube", "tiktok", "discord"].map(platform => (
                  <div key={platform} className="flex items-center gap-4 p-4 bg-[#1A1A1A] rounded-lg">
                    <Switch
                      checked={settings.social_media?.[platform]?.enabled || false}
                      onCheckedChange={(v) => setSettings({
                        ...settings,
                        social_media: {...settings.social_media, [platform]: {...(settings.social_media?.[platform] || {}), enabled: v}}
                      })}
                    />
                    <span className="text-white capitalize w-24">{platform}</span>
                    <Input
                      placeholder="URL"
                      value={settings.social_media?.[platform]?.url || ""}
                      onChange={(e) => setSettings({
                        ...settings,
                        social_media: {...settings.social_media, [platform]: {...(settings.social_media?.[platform] || {}), url: e.target.value}}
                      })}
                      className="flex-1 bg-[#121212] border-[#2A2A2A] text-white"
                    />
                    {platform === "telegram" && <span className="text-[#FF0099] text-xs">EN POPÜLER</span>}
                  </div>
                ))}
              </div>
            )}

            {activeTab === "telegram" && settings && (
              <div className="space-y-6">
                {/* Bot Durumu */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <Send className="w-5 h-5 text-[#0088cc]" /> Telegram Bot Durumu
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-[#1A1A1A] rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${settings.telegram_bot?.bot_active ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                          <div>
                            <p className="text-white font-display">Bot Servisi</p>
                            <p className="text-xs text-[#A1A1AA]">Telegram bot çalışma durumu</p>
                          </div>
                        </div>
                        <Switch
                          checked={settings.telegram_bot?.bot_active || false}
                          onCheckedChange={(v) => setSettings({...settings, telegram_bot: {...(settings.telegram_bot || {}), bot_active: v}})}
                        />
                      </div>
                    </div>
                    <div className="p-4 bg-[#1A1A1A] rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-display">Kanal Paylaşımı</p>
                          <p className="text-xs text-[#A1A1AA]">Kanala otomatik mesaj gönderimi</p>
                        </div>
                        <Switch
                          checked={settings.telegram_bot?.enabled || false}
                          onCheckedChange={(v) => setSettings({...settings, telegram_bot: {...(settings.telegram_bot || {}), enabled: v}})}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-[#0088cc]/10 border border-[#0088cc]/30 rounded-lg flex items-center gap-3">
                    <span className="text-2xl">🤖</span>
                    <div>
                      <p className="text-[#0088cc] font-mono">@PromocodeAI_bot</p>
                      <p className="text-xs text-[#A1A1AA]">https://t.me/PromocodeAI_bot</p>
                    </div>
                  </div>
                </div>

                {/* API Ayarları */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <KeyRound className="w-5 h-5 text-[#CCFF00]" /> API Ayarları
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Bot Token</Label>
                      <Input
                        type="password"
                        value={settings.telegram_bot?.token || ""}
                        onChange={(e) => setSettings({...settings, telegram_bot: {...(settings.telegram_bot || {}), token: e.target.value}})}
                        className="bg-[#121212] border-[#2A2A2A] text-white font-mono"
                        placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Kanal/Grup ID</Label>
                      <Input
                        value={settings.telegram_bot?.channel_id || ""}
                        onChange={(e) => setSettings({...settings, telegram_bot: {...(settings.telegram_bot || {}), channel_id: e.target.value}})}
                        className="bg-[#121212] border-[#2A2A2A] text-white font-mono"
                        placeholder="@kanaladi veya -100123456789"
                      />
                    </div>
                    <Button onClick={testTelegram} variant="outline" className="border-[#0088cc] text-[#0088cc]">
                      <Send className="w-4 h-4 mr-2" /> Test Mesajı Gönder
                    </Button>
                  </div>
                </div>

                {/* Karşılama Mesajı */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <MessageCircle className="w-5 h-5 text-[#FF0099]" /> Bot Karşılama Mesajı
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Hoş Geldin Mesajı (HTML destekli)</Label>
                      <Textarea
                        value={settings.telegram_bot?.welcome_message || ""}
                        onChange={(e) => setSettings({...settings, telegram_bot: {...(settings.telegram_bot || {}), welcome_message: e.target.value}})}
                        className="bg-[#121212] border-[#2A2A2A] text-white font-mono h-40"
                        placeholder="<b>Hoş Geldin!</b>&#10;Promosyon kodları için doğru yerdesin!"
                      />
                    </div>
                    <div className="p-3 bg-[#2A2A2A] rounded-lg">
                      <p className="text-xs text-[#A1A1AA] mb-2">HTML Etiketleri:</p>
                      <div className="flex flex-wrap gap-2 text-xs font-mono">
                        <span className="px-2 py-1 bg-[#3A3A3A] text-[#CCFF00] rounded">&lt;b&gt;kalın&lt;/b&gt;</span>
                        <span className="px-2 py-1 bg-[#3A3A3A] text-[#CCFF00] rounded">&lt;i&gt;italik&lt;/i&gt;</span>
                        <span className="px-2 py-1 bg-[#3A3A3A] text-[#CCFF00] rounded">&lt;code&gt;kod&lt;/code&gt;</span>
                        <span className="px-2 py-1 bg-[#3A3A3A] text-[#CCFF00] rounded">&lt;a href=""&gt;link&lt;/a&gt;</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reklam Ayarları */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <Zap className="w-5 h-5 text-[#00FFFF]" /> Reklam Alanı (GIF)
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                      <div>
                        <p className="text-white font-display">Reklam Aktif</p>
                        <p className="text-xs text-[#A1A1AA]">Bot içinde reklam gösterimi</p>
                      </div>
                      <Switch
                        checked={settings.telegram_bot?.ad_enabled || false}
                        onCheckedChange={(v) => setSettings({...settings, telegram_bot: {...(settings.telegram_bot || {}), ad_enabled: v}})}
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Reklam GIF URL</Label>
                      <Input
                        value={settings.telegram_bot?.ad_gif_url || ""}
                        onChange={(e) => setSettings({...settings, telegram_bot: {...(settings.telegram_bot || {}), ad_gif_url: e.target.value}})}
                        className="bg-[#121212] border-[#2A2A2A] text-white"
                        placeholder="https://media.giphy.com/media/.../giphy.gif"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Reklam Metni</Label>
                      <Input
                        value={settings.telegram_bot?.ad_text || ""}
                        onChange={(e) => setSettings({...settings, telegram_bot: {...(settings.telegram_bot || {}), ad_text: e.target.value}})}
                        className="bg-[#121212] border-[#2A2A2A] text-white"
                        placeholder="🎁 Reklam vermek için: marketing@promocode.dev"
                      />
                    </div>
                    {settings.telegram_bot?.ad_gif_url && (
                      <div className="p-4 bg-[#1A1A1A] rounded-lg">
                        <p className="text-xs text-[#A1A1AA] mb-2">Önizleme:</p>
                        <img 
                          src={settings.telegram_bot.ad_gif_url} 
                          alt="Reklam GIF" 
                          className="max-w-xs rounded-lg"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <p className="text-sm text-white mt-2">{settings.telegram_bot?.ad_text}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "scheduled" && (
              <div className="space-y-6">
                {/* Yeni Zamanlanmış Mesaj */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <Plus className="w-5 h-5 text-[#CCFF00]" /> Yeni Zamanlanmış Mesaj
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Mesaj İçeriği (HTML destekli)</Label>
                      <Textarea value={newPost.message} onChange={(e) => setNewPost({...newPost, message: e.target.value})} className="bg-[#121212] border-[#2A2A2A] text-white h-24" placeholder="🎁 Günün promosyon kodu: PROMO2026" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-[#A1A1AA]">Saat</Label>
                        <Input type="number" min="0" max="23" value={newPost.hour} onChange={(e) => setNewPost({...newPost, hour: parseInt(e.target.value) || 0})} className="bg-[#121212] border-[#2A2A2A] text-white" />
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA]">Dakika</Label>
                        <Input type="number" min="0" max="59" value={newPost.minute} onChange={(e) => setNewPost({...newPost, minute: parseInt(e.target.value) || 0})} className="bg-[#121212] border-[#2A2A2A] text-white" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Günler</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {[{id:"mon",label:"Pzt"},{id:"tue",label:"Sal"},{id:"wed",label:"Çar"},{id:"thu",label:"Per"},{id:"fri",label:"Cum"},{id:"sat",label:"Cmt"},{id:"sun",label:"Paz"}].map(day => (
                          <button key={day.id} onClick={() => setNewPost({...newPost, days: newPost.days.includes(day.id) ? newPost.days.filter(d => d !== day.id) : [...newPost.days, day.id]})} className={`px-3 py-1 rounded text-sm ${newPost.days.includes(day.id) ? 'bg-[#CCFF00] text-black' : 'bg-[#2A2A2A] text-[#A1A1AA]'}`}>
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <Button onClick={addScheduledPost} className="mt-4 btn-glow">
                    <Plus className="w-4 h-4 mr-2" /> Zamanlanmış Mesaj Ekle
                  </Button>
                </div>

                {/* Mesaj Listesi */}
                <div className="promo-card p-6">
                  <h2 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-[#00FFFF]" /> Zamanlanmış Mesajlar ({scheduledPosts.length})
                  </h2>
                  <div className="space-y-3">
                    {scheduledPosts.map(post => (
                      <div key={post.id} className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                        <div className="flex items-center gap-4">
                          <Switch checked={post.enabled} onCheckedChange={(v) => toggleScheduledPost(post.id, v)} />
                          <div>
                            <p className="text-white text-sm">{post.message.substring(0, 50)}{post.message.length > 50 ? '...' : ''}</p>
                            <p className="text-xs text-[#A1A1AA]">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {String(post.hour).padStart(2,'0')}:{String(post.minute).padStart(2,'0')} | 
                              Gönderim: {post.send_count || 0}x
                              {post.last_sent && ` | Son: ${new Date(post.last_sent).toLocaleString('tr-TR')}`}
                            </p>
                          </div>
                        </div>
                        <Button onClick={() => deleteScheduledPost(post.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {scheduledPosts.length === 0 && <p className="text-center text-[#A1A1AA] py-8">Henüz zamanlanmış mesaj yok</p>}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "chatbot" && settings && (
              <div className="promo-card p-6 space-y-6">
                <h2 className="font-display text-lg text-white uppercase flex items-center gap-2">
                  <Bot className="w-5 h-5 text-[#FF0099]" /> AI Canlı Destek
                </h2>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-white font-display">Chatbot Aktif</p>
                    <p className="text-xs text-[#A1A1AA]">7/24 AI destekli canlı destek</p>
                  </div>
                  <Switch
                    checked={settings.chatbot_enabled}
                    onCheckedChange={(v) => setSettings({...settings, chatbot_enabled: v})}
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Chatbot Bağlamı (AI'ın bilmesi gerekenler)</Label>
                  <Textarea
                    value={settings.chatbot_context || ""}
                    onChange={(e) => setSettings({...settings, chatbot_context: e.target.value})}
                    className="bg-[#121212] border-[#2A2A2A] text-white h-48"
                  />
                </div>
              </div>
            )}

            {activeTab === "stats" && stats && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="promo-card p-6">
                    <Users className="w-8 h-8 text-[#CCFF00] mb-2" />
                    <p className="text-3xl font-mono text-white">{stats.total_views}</p>
                    <p className="text-xs text-[#A1A1AA] uppercase">Toplam Kod Alim</p>
                  </div>
                  <div className="promo-card p-6">
                    <BarChart3 className="w-8 h-8 text-[#00FFFF] mb-2" />
                    <p className="text-3xl font-mono text-white">{stats.today_views}</p>
                    <p className="text-xs text-[#A1A1AA] uppercase">Bugun</p>
                  </div>
                  <div className="promo-card p-6">
                    <Gift className="w-8 h-8 text-[#FF0099] mb-2" />
                    <p className="text-xl font-mono text-[#CCFF00]">{campaigns.length}</p>
                    <p className="text-xs text-[#A1A1AA] uppercase">Aktif Kampanya</p>
                  </div>
                </div>
                <div className="promo-card p-6">
                  <h3 className="font-display text-white mb-4">Son Kod Alimlari (Telegram)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#2A2A2A]">
                          <th className="text-left py-2 text-[#A1A1AA]">Kullanici</th>
                          <th className="text-left py-2 text-[#A1A1AA]">Kampanya</th>
                          <th className="text-left py-2 text-[#A1A1AA]">Tarih</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recent_views?.map((v, i) => (
                          <tr key={i} className="border-b border-[#2A2A2A]/50">
                            <td className="py-2 font-mono text-white">{v.telegram_user_id || v.ip_address || '-'}</td>
                            <td className="py-2 text-[#CCFF00] text-xs">{v.campaign_name || v.fingerprint?.slice(0,12) || '-'}</td>
                            <td className="py-2 text-[#A1A1AA]">{new Date(v.claimed_at || v.viewed_at).toLocaleString("tr-TR")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Bot İstatistikleri */}
                {botStats && (
                  <div className="promo-card p-6">
                    <h3 className="font-display text-lg text-white uppercase flex items-center gap-2 mb-4">
                      <Bot className="w-5 h-5 text-[#0088cc]" /> Telegram Bot İstatistikleri
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-[#1A1A1A] rounded-lg text-center">
                        <p className="text-2xl font-mono text-white">{botStats.total_users}</p>
                        <p className="text-xs text-[#A1A1AA]">Toplam Kullanıcı</p>
                      </div>
                      <div className="p-4 bg-[#1A1A1A] rounded-lg text-center">
                        <p className="text-2xl font-mono text-white">{botStats.active_today}</p>
                        <p className="text-xs text-[#A1A1AA]">Bugün Aktif</p>
                      </div>
                      <div className="p-4 bg-[#1A1A1A] rounded-lg text-center">
                        <p className="text-2xl font-mono text-[#CCFF00]">{botStats.total_tokens_distributed}</p>
                        <p className="text-xs text-[#A1A1AA]">Dağıtılan Jeton</p>
                      </div>
                    </div>
                    
                    {/* Buton Tıklamaları */}
                    {Object.keys(botStats.button_clicks || {}).length > 0 && (
                      <div>
                        <p className="text-sm text-[#A1A1AA] mb-3">Buton Tıklamaları:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(botStats.button_clicks).map(([btn, count]) => (
                            <div key={btn} className="px-3 py-2 bg-[#2A2A2A] rounded-lg">
                              <span className="text-white text-sm capitalize">{btn.replace('_', ' ')}</span>
                              <span className="text-[#CCFF00] font-mono ml-2">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/miniapp" element={<TelegramMiniApp />} />
          <Route path="/admindev" element={<AdminLogin />} />
          <Route path="/admindev/dashboard" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
